/**
 * Per-case mock data for the Credit Checker tab.
 *
 * The call recording, call transcription, the structured "Case Summary — Call
 * notes" card and the GST/Ledger/Banking triangulation do not yet have backend
 * APIs. Until those ship, we mock them per caseId — matching the existing
 * per-caseId demo pattern used elsewhere in this codebase (see the demo caseId
 * lists in final-credit-summary.js).
 *
 * Wiring rule for callers: prefer real data when it exists, fall back to the
 * mock returned here. Swapping in a real API later means deleting the relevant
 * branch in the section component — the shapes below are the contract.
 */

const CASE_158262 = {
    // Four headline numbers shown as cards at the top of the Case Summary.
    topMetrics: [
        { label: "Proposed Limit", value: "19,29,716" },
        { label: "Avg. Monthly Business", value: "6,43,238" },
        { label: "Total Obligations", value: "13,93,683" },
        { label: "Requested Limit", value: "10,00,000" }
    ],

    // Verification call recording + transcript.
    call: {
        type: "Verification call",
        durationLabel: "14 min",
        // Placeholder audio so the player renders end-to-end. Replace with the
        // real signed recording URL once the backend exposes one.
        recordingUrl:
            "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
        transcript: [
            {
                speaker: "Agent",
                time: "00:12",
                text:
                    "Good afternoon, am I speaking with Mr. Sandeep Thakur? This is a verification call regarding your credit application."
            },
            {
                speaker: "Borrower",
                time: "00:18",
                text:
                    "Yes, Sandeep here. Please go ahead, I have about fifteen minutes."
            },
            {
                speaker: "Agent",
                time: "00:31",
                text:
                    "Thank you. Could you confirm the nature of your business and how long you have been running it?"
            },
            {
                speaker: "Borrower",
                time: "00:39",
                text:
                    "We do wholesale trading of agri equipment, hand tools and spare pumps. The firm has been running since 2000, so over 25 years in the same line."
            },
            {
                speaker: "Agent",
                time: "01:05",
                text:
                    "Understood. And what is your approximate monthly business volume currently?"
            },
            {
                speaker: "Borrower",
                time: "01:11",
                text:
                    "Roughly six to six and a half lakh a month on average across the year."
            }
        ],
        // AI analysis of the verification call (mock until the speech-analysis
        // API ships). `tone.sentiment`: positive | neutral | negative.
        transcriptAnalysis: {
            summary:
                "Borrower confirmed identity and business profile without hesitation — wholesale trading of agri equipment, hand tools and spare pumps, running 25+ years. Declared monthly volume of ~₹6–6.5 L aligns with the application. Overall the responses were consistent and forthcoming, with minor vagueness on exact GST figures and a mention of cash sales.",
            tone: { label: "Cooperative & confident", sentiment: "positive" },
            good: [
                "Identity and business nature confirmed promptly and clearly.",
                "Long vintage (25+ years, since 2000) corroborated on the call.",
                "Stated monthly turnover (~₹6.4 L) matches the declared figures."
            ],
            bad: [
                "Did not have exact GST turnover numbers on hand during the call.",
                "Vague when asked about number of employees and current stock."
            ],
            ugly: [
                "Acknowledged a portion of sales is collected in cash and not fully banked — explains the low bank-to-GST coverage.",
                "Brief hesitation when asked about an existing loan obligation before confirming it."
            ]
        }
    },

    // Structured "Case Summary — Call notes" card.
    summary: {
        borrower: [
            { label: "Constitution", value: "Proprietorship — Sandeep Thakur" },
            { label: "Age", value: "48 years" },
            {
                label: "Family",
                value: "Father retired · spouse housewife · 3 children studying"
            },
            { label: "Other businesses", value: "None" }
        ],
        business: [
            {
                label: "Profile",
                value:
                    "Wholesale trading — agri equipment, hand tools, spare pumps (RT-100)"
            },
            { label: "Inception", value: "Since 2000 · GST since Jul 2017" },
            { label: "Experience", value: "25+ years, same line" },
            {
                label: "Other brands",
                value: "Paras Miracle Poly 19%, Neptune 7%, Green 6%, rest others"
            },
            { label: "ASM / distance / vintage", value: "Dilip · 30 km · 4 yrs" }
        ],
        numbers: [
            { label: "Avg. monthly business", value: "₹6.43 L (12-mo purchase data)" },
            { label: "Topline FY'25 / FY'26", value: "₹1.23 Cr / ₹81.41 L (GST)" },
            { label: "Billings FY'26 (ledger)", value: "₹19.30 L" },
            {
                label: "Banking — SBI CA (6m)",
                value: "T/O ₹35.06 L · avg balance ₹1.08 L"
            },
            { label: "OD/CC", value: "None running" }
        ],
        credit: [
            { label: "CIBIL", value: "789 — Good" },
            {
                label: "Live obligations",
                value:
                    "BL 7L · 15.3L · 21.72L · 22.44L · Auto 11.14L · HL 45L (LIC) · HL 34.85L (TATA) · PL 5L"
            },
            { label: "LOR", value: "Approved Jan'26 (low LOR letter on file)" },
            { label: "Dedupe", value: "No match" }
        ]
    },

    // Heuristic call analysis. Mirrors the shape of the real "Lumi's View" AI
    // payload so the analysis card can render either source identically.
    analysis: {
        final_decision: "Approve",
        suggested_safe_limit: "₹10,00,000",
        sentiment: "Positive",
        short_case_summary: [
            "Established proprietorship, 25+ years in agri-equipment wholesale.",
            "Stable family profile; no competing businesses.",
            "GST topline and ledger billings broadly consistent with declared volumes.",
            "Clean dedupe; CIBIL 789 with multiple serviced obligations."
        ],
        rationale: [
            "Long vintage and single-line focus reduce business risk.",
            "Banking turnover supports the declared monthly business.",
            "Obligation load is high but historically serviced without delinquency.",
            "Recommend approval at requested limit with standard monitoring."
        ]
    },

    // GST / Ledger / Banking triangulation. The monthly series, reconciliation
    // percentages and auto-flags are the base view; the builder overlays real
    // ledger sales, bank credits, ARP and ledger repayment when those exist.
    // Values are in ₹ lakhs.
    triangulation: {
        // 12 aligned months. `ledger` is ledger SALES (reconciles vs GST);
        // `ledgerRepayment` is ledger REPAYMENTS (reconciles vs `bank` credits /
        // BSA); `bank` is monthly bank credits. Numbers carry natural month-to-
        // month variance and are internally consistent: ledger sales sit within
        // ~1-3% of GST (sometimes above, sometimes below); bank credits run
        // ~42-50% of GST (cash-heavy); ledger repayment is set so bank captures
        // ~68-75% of it. The reconciliation %s below are DERIVED from these.
        monthly: [
            { month: "Jul'25", gst: 11.8, ledger: 11.5, ledgerRepayment: 7.3, bank: 5.4 },
            { month: "Aug'25", gst: 9.6, ledger: 9.9, ledgerRepayment: 6.1, bank: 4.3 },
            { month: "Sep'25", gst: 8.4, ledger: 8.2, ledgerRepayment: 5.2, bank: 3.6 },
            { month: "Oct'25", gst: 13.7, ledger: 14.0, ledgerRepayment: 9.1, bank: 6.8 },
            { month: "Nov'25", gst: 7.1, ledger: 6.8, ledgerRepayment: 4.3, bank: 3.0 },
            { month: "Dec'25", gst: 6.2, ledger: 6.4, ledgerRepayment: 3.9, bank: 2.9 },
            { month: "Jan'26", gst: 8.9, ledger: 8.6, ledgerRepayment: 5.6, bank: 4.1 },
            { month: "Feb'26", gst: 9.4, ledger: 9.7, ledgerRepayment: 6.0, bank: 4.2 },
            { month: "Mar'26", gst: 12.3, ledger: 12.0, ledgerRepayment: 7.9, bank: 5.9 },
            { month: "Apr'26", gst: 8.1, ledger: 8.4, ledgerRepayment: 5.1, bank: 3.5 },
            { month: "May'26", gst: 7.4, ledger: 7.1, ledgerRepayment: 4.7, bank: 3.4 },
            { month: "Jun'26", gst: 5.9, ledger: 5.7, ledgerRepayment: 3.6, bank: 2.5 }
        ],
        // Captions shown under each reconciliation bar.
        captions: {
            gst_ledger: "Sales reconcile within ~1% — GST turnover matches the Tally ledger",
            ledger_bank: "Bank captures ~72% of ledger repayments — remainder via cash/other modes",
            gst_bank: "Only ~46% of sales are banked — cash-heavy; pull the cash book"
        },
        // ARP fallback (check 3) when avg_repayment_period is unavailable.
        arpFallback: { value: "42 days", note: "Avg. repayment period" },
        // Narrative auto-flags.
        flags: [
            {
                tone: "warn",
                text:
                    "FY'25 turnover ₹1.23 Cr → FY'26 trending to ₹81.4 L · ~34% dip (heavy monsoon impact, agri season)."
            },
            {
                tone: "warn",
                text:
                    "Bank credits cover only ~46% of GST topline. Likely cash receipts — request cash ledger for triangulation."
            },
            {
                tone: "good",
                text:
                    "GST and Tally ledger reconciled within ~0.5%. No round-tripping or fake-invoice pattern detected."
            }
        ]
    },

    // Per-applicant CIBIL detail. The score gauge, band, pulled date, bureau
    // name and report PDF come from real data when available; these granular
    // analysis tiles have no clean structured API (they live only inside the
    // bureau report HTML), so they are mocked here for case 158262.
    cibil: {
        // Used only when a real cibil_score is missing for the applicant.
        fallbackScore: 675,
        fallbackVersion: "CIBIL V3",
        fallbackPulledLabel: "28 May 2026",
        // Summary-of-analysis tiles (Parameter → Output). `tone` drives colour:
        // good=green, warn=amber, bad=red, neutral=default.
        tiles: [
            { label: "Suit-Filed (24M)", value: "0", tone: "good" },
            { label: "Write-Off / Settled (12M)", value: "0", tone: "good" },
            { label: "Sub/LSS/DBT/SMA (12M)", value: "0", tone: "good" },
            { label: "30+DPD (6M)", value: "0", tone: "good" },
            { label: "90+DPD (12M)", value: "0", tone: "good" },
            { label: "PL/BL Enquiries (3M)", value: "0", tone: "good" },
            { label: "Current Overdue", value: "₹20.0 L", tone: "warn" },
            { label: "DPD ≤ 30 (12M)", value: "3", tone: "warn" },
            { label: "Min Score Policy", value: "700", tone: "neutral" }
        ]
    }
};

const MOCK_BY_CASE_ID = {
    158262: CASE_158262
};

/**
 * Returns the Credit Checker mock payload for a case, or null if none exists.
 * @param {number|string} caseId
 */
export function getCreditCheckerMock(caseId) {
    return MOCK_BY_CASE_ID[Number(caseId)] || null;
}

/**
 * Returns the CIBIL detail mock (gauge fallback + analysis tiles) for a case,
 * or null if none exists.
 * @param {number|string} caseId
 */
export function getCibilMock(caseId) {
    const mock = MOCK_BY_CASE_ID[Number(caseId)];
    return (mock && mock.cibil) || null;
}

export default getCreditCheckerMock;
